/**
 * Escolher QUAIS grupos monitorar.
 *
 * Antes esta tela só perguntava de qual número puxar e importava tudo. Quem tem 200+
 * grupos no WhatsApp pessoal — família, trabalho, condomínio, três comunidades de
 * impressão 3D — não quer monitorar 200: quer marcar cinco. "Tudo ou nada" estava errado
 * para o produto, não só para o teste.
 *
 * Quatro decisões de tela que valem o comentário:
 *
 *  - **Os já monitorados continuam na lista, marcados.** Some-los responderia "por que o
 *    meu grupo não aparece para escolher?" com o silêncio. Eles aparecem com o crachá
 *    "Monitorando" e com a saída — "Deixar de monitorar" — do lado.
 *  - **Deixar de monitorar guarda o histórico.** As entradas e saídas já registradas
 *    continuam de pé, e o grupo volta com "Voltar a monitorar". A coluna se chama
 *    `deleted_at` e o estado se chama `ARCHIVED` — nomes de implementação que NÃO aparecem
 *    aqui nem na lista: o rótulo sai de `GROUP_ACTION`/`GROUP_STATUS`, escrito uma vez só.
 *    Foi o produto falar duas línguas para o mesmo ato que fez o dono não achar o caminho.
 *  - **Lista incompleta é dita, não escondida.** Quando o provider não deixa varrer até o
 *    fim, o aviso aparece — 100 de 250 mostrados calados seria um número redondo mentindo.
 *  - **Busca aguenta acento e ordem trocada** (ver `group-search.ts`): `energia projeto`
 *    acha `[M1/12] SDS.ADTN - Projeto Energia`, e `fabio` acha `#Fábio Furtado`.
 *
 * Mobile-first (DESIGN_SYSTEM §11): a ação principal é botão de largura cheia e cada
 * linha da lista tem alvo de toque de 44px.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  NO_DATA_DASH,
  Spinner,
} from '@mgdlead/ui';
import { Search, Users } from 'lucide-react';

import { Checkbox } from '@mgdlead/ui';
import { SelectAllBar } from '../../../features/monitor/SelectAllBar';
import { FormAlert } from '@mgdlead/ui';
import { NativeSelect } from '@mgdlead/ui';
import { GROUP_ACTION, GROUP_STATUS, GROUP_WITHOUT_NAME } from '../../../features/group/group-ui';
import { matchesSearch } from '../../../features/group/group-search';
import { useAvailableGroups } from '../../../lib/api';
import type { AvailableGroup, Monitor } from '../../../lib/api';
import { humanizeError } from '../../../lib/errors';

export interface ImportGroupsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  monitors: readonly Monitor[];
  pending: boolean;
  /** Deixar de/voltar a monitorar em curso — trava só os botões de linha. */
  rowPending: boolean;
  onConfirm: (monitorId: string, providerGroupIds: string[]) => void;
  /** Deixar de monitorar: tira da operação e guarda o histórico. Recebe o NOSSO id. */
  onStopMonitoring: (groupId: string, name: string) => void;
  /** Voltar a monitorar: religa um grupo que estava fora da operação. */
  onResumeMonitoring: (groupId: string, name: string) => void;
}

/** Só número conectado consegue listar grupo. O resto entra desabilitado, com o porquê. */
export function isImportable(monitor: Monitor): boolean {
  return monitor.status === 'CONNECTED' || monitor.status === 'DEGRADED';
}

/** O nome que a linha mostra. `null` é "o WhatsApp não nos disse", não "sem nome". */
export function availableGroupName(group: Pick<AvailableGroup, 'name'>): string {
  return group.name ?? GROUP_WITHOUT_NAME;
}

export function ImportGroupsDialog({
  open,
  onOpenChange,
  workspaceId,
  monitors,
  pending,
  rowPending,
  onConfirm,
  onStopMonitoring,
  onResumeMonitoring,
}: ImportGroupsDialogProps) {
  const disponiveis = monitors.filter(isImportable);
  const primeiro = disponiveis[0]?.id ?? '';

  const [monitorId, setMonitorId] = useState<string>(primeiro);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const idsDosMonitoradores = monitors.map((m) => m.id).join(',');
  useEffect(() => {
    if (!open) return;
    setMonitorId(primeiro);
    setQuery('');
    setSelected(new Set());
    // `disponiveis` é recriado a cada render; a lista de ids é o que de fato muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idsDosMonitoradores]);

  // Trocar de número troca a lista inteira: seleção do número anterior não se carrega.
  useEffect(() => {
    setSelected(new Set());
    setQuery('');
  }, [monitorId]);

  const listaQuery = useAvailableGroups(workspaceId, open && monitorId !== '' ? monitorId : null);
  const lista = listaQuery.data;
  const grupos = useMemo(() => lista?.groups ?? [], [lista]);

  /** O que a busca deixou passar. É sobre esta lista que "marcar todos" age. */
  const encontrados = useMemo(
    () => grupos.filter((group) => matchesSearch(group, query)),
    [grupos, query],
  );

  /** Marcável = ainda não está aqui dentro. Monitorado e não monitorado têm caminho próprio. */
  const marcaveis = useMemo(() => encontrados.filter((g) => !g.imported), [encontrados]);
  const marcadosVisiveis = marcaveis.filter((g) => selected.has(g.providerGroupId)).length;

  function toggle(providerGroupId: string) {
    setSelected((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(providerGroupId)) proximo.delete(providerGroupId);
      else proximo.add(providerGroupId);
      return proximo;
    });
  }

  /** "Selecionar todos" age sobre o que a busca deixou passar (e que ainda não está aqui). */
  function marcarVisiveis(marcar: boolean) {
    setSelected((atual) => {
      const proximo = new Set(atual);
      for (const group of marcaveis) {
        if (marcar) proximo.add(group.providerGroupId);
        else proximo.delete(group.providerGroupId);
      }
      return proximo;
    });
  }

  const totalSelecionado = selected.size;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Escolher grupos para monitorar"
      /* Curta de propósito: no celular cada linha de descrição come uma linha da lista. */
      description="A lista vem do WhatsApp. Marque só os que você quer acompanhar."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            block
            loading={pending}
            loadingLabel="Importando"
            disabled={totalSelecionado === 0 || monitorId === ''}
            onClick={() => onConfirm(monitorId, [...selected])}
          >
            {totalSelecionado === 0
              ? 'Monitorar os grupos marcados'
              : `Monitorar ${totalSelecionado} ${totalSelecionado === 1 ? 'grupo' : 'grupos'}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* -------------------------------- número -------------------------------- */}
        {monitors.length > 1 ? (
          <Field
            label="Número"
            hint="Só números conectados conseguem listar os grupos."
            htmlFor="import-monitor"
          >
            {(control) => (
              <NativeSelect
                {...control}
                id="import-monitor"
                value={monitorId}
                onChange={(event) => setMonitorId(event.target.value)}
              >
                {monitors.map((monitor) => (
                  <option key={monitor.id} value={monitor.id} disabled={!isImportable(monitor)}>
                    {monitor.name}
                    {isImportable(monitor) ? '' : ' — não está conectado'}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        ) : null}

        {/* --------------------------------- busca -------------------------------- */}
        <Field label="Buscar pelo nome" htmlFor="busca-grupo">
          {(control) => (
            <Input
              {...control}
              id="busca-grupo"
              type="search"
              leadingIcon={<Search />}
              placeholder="Ex.: ofertas, projeto energia, comunidade"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          )}
        </Field>

        {listaQuery.isPending ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Spinner />
            Lendo a lista de grupos do WhatsApp…
          </div>
        ) : listaQuery.isError ? (
          <ErrorState
            title="Não conseguimos ler os grupos deste número"
            description={humanizeError(listaQuery.error)}
            onRetry={() => void listaQuery.refetch()}
          />
        ) : (
          <>
            {/* -------------------------- o que a lista é -------------------------- */}
            {lista !== undefined && !lista.complete ? (
              <FormAlert tone="warning">
                Esta lista está incompleta: conseguimos ler {lista.seen} grupos antes de o WhatsApp
                parar de responder. Importe os que aparecem e atualize a lista de novo para ver o
                resto.
              </FormAlert>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex flex-wrap items-center gap-1">
                <Badge tone={totalSelecionado > 0 ? 'success' : 'neutral'} numeric dot>
                  {totalSelecionado} {totalSelecionado === 1 ? 'marcado' : 'marcados'}
                </Badge>
                {lista !== undefined && lista.importedCount > 0 ? (
                  <Badge tone="neutral" numeric>
                    {lista.importedCount} já{' '}
                    {lista.importedCount === 1 ? 'monitorado' : 'monitorados'}
                  </Badge>
                ) : null}
                <Badge tone="neutral" numeric>
                  {encontrados.length} de {grupos.length} na lista
                </Badge>
              </span>
            </div>

            <SelectAllBar
              visible={marcaveis.length}
              selectedVisible={marcadosVisiveis}
              selectedTotal={totalSelecionado}
              filtered={query.trim() !== ''}
              disabled={pending}
              onSelectVisible={() => marcarVisiveis(true)}
              onUnselectVisible={() => marcarVisiveis(false)}
              onClear={() => setSelected(new Set())}
            />

            {/* --------------------------------- lista -------------------------------- */}
            {grupos.length === 0 ? (
              <EmptyState
                kind="no-data"
                icon={Users}
                compact
                title="Este número não está em nenhum grupo"
                description="Entre em um grupo pelo WhatsApp e atualize a lista — nós não criamos grupo nenhum."
              />
            ) : encontrados.length === 0 ? (
              <EmptyState
                /* `zero` e não `no-data`: a lista FOI medida e o filtro não achou nada. */
                kind="zero"
                icon={Search}
                compact
                title="Nenhum grupo com esse nome"
                description={`Nenhum dos ${grupos.length} grupos deste número casa com “${query}”. Tente uma palavra só.`}
              />
            ) : (
              /* `dvh` e não `px`: 320px fixos sobram no desktop e engolem a lista no celular. */
              <ul className="max-h-[45dvh] overflow-y-auto divide-y divide-border rounded-lg border border-border">
                {encontrados.map((group) => (
                  <li key={group.providerGroupId}>
                    <AvailableGroupRow
                      group={group}
                      checked={selected.has(group.providerGroupId)}
                      rowPending={rowPending}
                      onToggle={() => toggle(group.providerGroupId)}
                      onStopMonitoring={onStopMonitoring}
                      onResumeMonitoring={onResumeMonitoring}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

/* ========================================================================== */
/*  A linha                                                                    */
/* ========================================================================== */

interface RowProps {
  group: AvailableGroup;
  checked: boolean;
  rowPending: boolean;
  onToggle: () => void;
  onStopMonitoring: (groupId: string, name: string) => void;
  onResumeMonitoring: (groupId: string, name: string) => void;
}

function AvailableGroupRow({
  group,
  checked,
  rowPending,
  onToggle,
  onStopMonitoring,
  onResumeMonitoring,
}: RowProps) {
  const nome = availableGroupName(group);

  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2 max-md:min-h-11">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {group.imported ? (
          // Já está dentro: não há o que marcar. O crachá explica, e a ação é a saída.
          <span className={group.name === null ? 'text-muted-foreground' : 'font-medium'}>
            {nome}
          </span>
        ) : (
          <Checkbox
            checked={checked}
            onChange={onToggle}
            label={
              <span className={group.name === null ? 'text-muted-foreground' : undefined}>
                {nome}
              </span>
            }
          />
        )}

        <span className="flex flex-wrap items-center gap-1 text-2xs text-muted-foreground">
          {/* `null` é travessão: "não sei quantos são" não é "grupo vazio" (invariante 8). */}
          <span className="nums">
            {group.participantsCount === null
              ? `${NO_DATA_DASH} sem contagem`
              : `${group.participantsCount} ${group.participantsCount === 1 ? 'pessoa' : 'pessoas'}`}
          </span>
          {group.full === true ? (
            <Badge tone="warning" dot title="Já passou do seu limite de segurança.">
              Cheio
            </Badge>
          ) : null}
          {group.archived ? (
            <Badge tone="neutral">{GROUP_STATUS.ARCHIVED.label}</Badge>
          ) : group.imported ? (
            <Badge tone="success" dot>
              Monitorando
            </Badge>
          ) : null}
        </span>
      </div>

      {group.imported && group.groupId !== null ? (
        group.archived ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={rowPending}
            aria-label={`${GROUP_ACTION.resume} ${nome}`}
            onClick={() => onResumeMonitoring(group.groupId as string, nome)}
          >
            {GROUP_ACTION.resume}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            disabled={rowPending}
            aria-label={`${GROUP_ACTION.stop} ${nome}`}
            onClick={() => onStopMonitoring(group.groupId as string, nome)}
          >
            {GROUP_ACTION.stop}
          </Button>
        )
      ) : null}
    </div>
  );
}
